/// @ts-check
/**
 * StickerWorkspace 模式的對話歷史構建模組
 * 
 * 策略：與 Sticker 模式相同，從父節點基於邊追溯到 START 節點，構建完整對話脈絡
 */

import { buildUnifiedGroupContext } from '../../../utils/groupContextHelper';
import { getContextBuildParams, getGroupId } from '../../../utils/storageAccessor';
import { buildConversationHistoryBatch, shouldIncludeNode } from '../../../utils/conversationHistoryHelper';
import { ConversationHistory, GroupContextValidator } from '../../../types/groupContext.types.js';
import { traceToRoot } from '../core/CoreForumUtils';
import { extractTimestamp } from '../../../utils/nodeDataExtractor';

/**
 * @typedef {import('../../../types/groupContext.types.js').GroupContext} GroupContext
 * @typedef {import('../../../types/groupContext.types.js').ConversationHistoryItem} ConversationHistoryItem
 */

/**
 * 構建 Workspace 模式的小組 context（包含對話歷史）
 * @param {Array<any>} graphNodes - 圖形節點（需包含 _edges 和 _currentNodeId 臨時字段）
 * @param {string} currentIdea - 保留接口兼容性，未使用
 * @param {string} [tag=''] - 保留接口兼容性，未使用
 * @returns {GroupContext} 完整的小組 context
 */
export const buildWorkspaceContext = (graphNodes, currentIdea, tag = '') => {
  let limit = 8;
  
  const params = getContextBuildParams();
  const baseContext = buildUnifiedGroupContext(graphNodes, params, 'sticker');
  
  // 從臨時字段提取追溯所需數據
  const currentNodeId = graphNodes[0]?._currentNodeId || null;
  const edges = graphNodes[0]?._edges || [];
  
  // ✅ 使用 ConversationHistory 類構建對話歷史
  const conversationHistory = buildConversationHistory(graphNodes, edges, currentNodeId, limit);
  
  console.log('[Workspace Context] 構建完成的 conversationHistory:', {
    count: conversationHistory.getCount(),
    items: conversationHistory.toArray()
  });
  
  // ✅ 使用 GroupContextValidator 創建 context，確保類型正確
  return GroupContextValidator.createWithHistory(
    {
      ...baseContext,
      interactionType: 'idea-creation',
      expectation: '深度思考和想法建構',
      contextVersion: '3.0-workspace'
    },
    conversationHistory
  );
};

/**
 * 構建對話歷史：從指定節點追溯到 START
 * ✅ 使用 ConversationHistory 類，提供類型安全和便捷操作
 * 
 * @param {Array<any>} nodes - 所有節點
 * @param {Array<any>} edges - 所有邊
 * @param {string|number|null} startNodeId - 追溯起點（通常是父節點）
 * @param {number} limit - 歷史記錄數量限制
 * @returns {ConversationHistory} ConversationHistory 實例
 */
const buildConversationHistory = (nodes, edges, startNodeId, limit) => {
  if (!nodes?.length) return ConversationHistory.empty();

  // 確定追溯起點（如未指定則使用最新節點）
  const traceFrom = startNodeId || findLatestNodeId(nodes);
  
  // 基於邊追溯路徑
  const tracedPath = traceToRoot(String(traceFrom), edges, nodes);
  
  // 過濾屬於當前 group 的節點
  const groupId = getGroupId();
  const filteredNodes = tracedPath.filter(node => shouldIncludeNode(node, groupId));
  
  // ✅ 使用模組化批量構建方法（自動驗證）
  const rawHistory = buildConversationHistoryBatch(filteredNodes, {
    useFullText: false,
    validate: true // 啟用類型驗證
  });

  // ✅ 使用 ConversationHistory 類包裝，提供鏈式操作
  const history = ConversationHistory
    .fromArray(rawHistory)
    .deduplicate()        // 去重
    .sortByTimestamp('desc')  // 按時間排序（最新在前）
    .limit(limit);        // 限制數量

  console.log(`[Workspace Context] 追溯: ${traceFrom} → ${tracedPath.length} 節點 → ${filteredNodes.length} 過濾 → ${history.getCount()} 條驗證通過的歷史`);
  
  return history;
};

/**
 * 查找最新的節點 ID
 * @param {Array<any>} nodes - 節點列表
 * @returns {string|number|null} 最新節點 ID
 */
const findLatestNodeId = (nodes) => {
  const sorted = [...nodes].sort((a, b) => extractTimestamp(b) - extractTimestamp(a));
  return sorted[0]?.id || null;
};

export const workspaceContext = {
  buildContext: buildWorkspaceContext,
};

export default workspaceContext;
